/**
 * /organic — sales page (landing) pod plan Organic.
 * Hero → jak działa → funkcje (Inspiracje/reelsy/grafiki) → pricing 99 zł → CTA.
 * CTA prowadzi do /trial z prefillem planu Organic (rejestracja → checkout).
 * Branding VP: dark, akcent emerald (Organic).
 */
import { Button } from "@/components/ui/button";
import {
  Flame,
  Wand2,
  Image as ImageIcon,
  Calendar,
  Check,
  ArrowRight,
  Sparkles,
  TrendingUp,
} from "lucide-react";

const CHECKOUT_HREF = "/trial?plan=start&vertical=content_creation&cycle=monthly";

const STEPS = [
  {
    icon: <Flame className="w-5 h-5" />,
    title: "Wybierz niszę",
    desc: "Podajesz swoją niszę i rynki. Codziennie zaciągamy viralowe formaty zza granicy — te które realnie zadziałały (outliery, nie przypadki).",
  },
  {
    icon: <Wand2 className="w-5 h-5" />,
    title: "Zrób reelsa jednym tapem",
    desc: "Z każdej viralki generujesz gotowy scenariusz pod Twoją markę — hook, struktura, adaptacja. Zero pustej kartki.",
  },
  {
    icon: <Calendar className="w-5 h-5" />,
    title: "Publikuj i skaluj",
    desc: "Scheduler, grafiki AI i analityka w jednym miejscu. Cała maszyna organicznego contentu.",
  },
];

const FEATURES = [
  { icon: <Flame className="w-5 h-5" />, title: "Feed Inspiracji (daily viral)", desc: "Codzienne viralki pod Twoją niszę, sortowane po sile (viral score), nie po dacie." },
  { icon: <Wand2 className="w-5 h-5" />, title: "Generator skryptów i hooków", desc: "Hook, scenariusz i adaptacja pod Twój ton i ofertę — z realnych viralowych formatów." },
  { icon: <ImageIcon className="w-5 h-5" />, title: "Generacja grafik AI", desc: "Grafiki, karuzele i posty na social — bez grafika, bez Canvy." },
  { icon: <Calendar className="w-5 h-5" />, title: "Scheduler + Analityka", desc: "Zaplanuj publikacje i śledź co działa. Wszystko w zakładce Organic." },
];

const PLAN_BULLETS = [
  "Feed Inspiracji — daily viral pod niszę",
  "„Zrób reelsa” z każdej viralki",
  "Generacja grafik AI",
  "Scheduler publikacji",
  "Asystent wiadomości + Analityka",
  "900 tokenów/mies + doładowania",
];

export default function OrganicSalesPage() {
  return (
    <div className="min-h-screen bg-background text-white overflow-x-hidden">
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(900px 500px at 70% -10%, rgba(16,185,129,0.12), transparent 60%)" }} />
        <div className="relative max-w-5xl mx-auto px-6 pt-24 pb-16 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-xs font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Nowość: zakładka Organic
          </span>
          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-balance mb-5">
            Organiczny content <span className="text-emerald-400">na autopilocie</span>
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto mb-9">
            Codziennie dostajesz sprawdzone viralowe formaty zza granicy pod Twoją niszę — i jednym
            tapem robisz z nich reelsa pod swoją markę. Koniec z pustą kartką.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href={CHECKOUT_HREF}>
              <Button className="h-12 px-7 text-base font-bold bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white">
                Zacznij za 99 zł/mies <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="/organic-plan">
              <Button variant="outline" className="h-12 px-7 text-base font-semibold border-white/15 bg-white/[0.03] hover:bg-white/[0.08] text-white">
                Dobierz plan pod siebie
              </Button>
            </a>
          </div>
          <p className="text-white/35 text-xs mt-4">14 dni za darmo · bez karty · anuluj w każdej chwili</p>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-center mb-2">Jak to działa</h2>
        <p className="text-white/50 text-center mb-12">Od zera do opublikowanego reelsa — w kilka minut</p>
        <div className="grid md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <div key={i} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 grid place-items-center">{s.icon}</span>
                <span className="text-emerald-400/60 font-extrabold text-lg tabular-nums">0{i + 1}</span>
              </div>
              <h3 className="font-bold text-lg mb-1.5">{s.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="flex gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6">
              <span className="w-11 h-11 shrink-0 rounded-xl bg-emerald-500/15 text-emerald-400 grid place-items-center">{f.icon}</span>
              <div>
                <h3 className="font-bold text-base mb-1">{f.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-md mx-auto px-6 py-16">
        <div className="relative rounded-3xl border border-emerald-500 bg-gradient-to-b from-emerald-500/[0.08] to-transparent p-8">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-2xl font-extrabold">Organic</h3>
            <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold"><TrendingUp className="w-3.5 h-3.5" /> daily viral</span>
          </div>
          <p className="text-white/55 text-sm mb-5">Cała zakładka Organic + Inspiracje</p>
          <div className="flex items-baseline gap-2 mb-6">
            <span className="text-5xl font-extrabold tracking-tight">99 zł</span>
            <span className="text-white/50 text-sm">/mies netto</span>
          </div>
          <ul className="space-y-2.5 mb-7">
            {PLAN_BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-white/80 text-sm">
                <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{b}
              </li>
            ))}
          </ul>
          <a href={CHECKOUT_HREF}>
            <Button className="w-full h-12 text-base font-bold bg-emerald-500 hover:bg-emerald-400 text-black">
              Zacznij teraz
            </Button>
          </a>
          <p className="text-center text-white/35 text-xs mt-3">Rocznie -20% (79 zł/mies) · doładowania tokenów na życzenie</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-balance mb-4">
          Przestań zgadywać co nagrać
        </h2>
        <p className="text-white/60 mb-8">Pozwól viralom zza granicy robić research za Ciebie.</p>
        <a href={CHECKOUT_HREF}>
          <Button className="h-12 px-8 text-base font-bold bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white">
            Zacznij za 99 zł/mies <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </a>
      </section>
    </div>
  );
}
